import type { ReactNode } from 'react'
import { MoreVertical, Pencil } from 'lucide-react'
import { ActionMenu, Button, type DataTableColumn } from '@atrvd/saas-ui'
import { formatCurrency, formatDate, formatDateTime } from '@dcp/shared/utils/formatters'
import { formatAccountNumber } from '@/components/ledgersync/connectionManagement.utils'
import type { CreditCardAvailabilityRow } from '@/lib/db-tenant'
import { DataWarningIcon } from '@/components/banking/DataWarningIcon'
import { ViewTransactionsIcon } from '@/components/banking/ViewTransactionsIcon'
import {
  CreditCardNote,
  CreditCardSourceBadge,
  ManuallyEditedValue,
  PrintedStatementBalance,
} from '@/components/banking/credit-cards/CreditCardCells'
import {
  manualEditTooltip,
  type CreditCardValueField,
} from '@/components/banking/credit-cards/creditCardManualValues'
import { creditCardConnectionWarning } from './creditCardConnectionWarning'

const STATEMENT_BALANCE_HINT =
  'Still owed on the last statement. When the bank has not reported what remains, this is the printed statement total.'

const CREDIT_LIMIT_HINT = 'The maximum amount that can be owed from the bank.'

const BALANCE_HINT =
  'The total amount owed on the card right now — the sum of all transactions charged to it to date. This is card usage, not cash on hand.'

const AVAILABILITY_HINT =
  'How much is still left to spend on the card before the payoff: Total Credit Limit − Current Balance.'

const PAYMENT_DUE_DATE_HINT =
  'The date the statement balance should be paid. This date is used to plan when funds must be available. If the date received from the bank differs from the one in the card portal or on the statement, it can be corrected manually.'

interface CreditCardColumnsOptions {
  /** The viewer has `manage_banking`: the row kebab offers Edit. */
  canManage: boolean
  /** Opens "Edit credit card" for the card id. */
  onEdit: (id: string) => void
  /** Opens "View transactions" for the card id. */
  onViewTransactions: (id: string) => void
}

/**
 * The Credit Card Availability table's columns.
 *
 * Column widths are explicit so the nine money columns keep their headers on
 * ONE line and the table scrolls sideways instead of squeezing "Current
 * Balance (Usage)" into three stacked words. `whitespace-nowrap` on the
 * header is what makes the width a floor rather than a suggestion.
 */
export function creditCardColumns({
  canManage,
  onEdit,
  onViewTransactions,
}: CreditCardColumnsOptions): DataTableColumn<CreditCardAvailabilityRow>[] {
  return [
    {
      key: 'facility',
      header: <span className="whitespace-nowrap">Facility</span>,
      headerLabel: 'Facility',
      sortable: true,
      minWidth: '140px',
      render: (row) => row.facility_name,
    },
    {
      key: 'name',
      header: <span className="whitespace-nowrap">Credit Card</span>,
      headerLabel: 'Credit Card',
      sortable: true,
      disableHide: true,
      minWidth: '220px',
      // Name plus the last four, whatever the source: a linked card's vendor
      // `mask` is at most four digits already, a manual card carries whatever
      // was typed in Banking Settings. `formatAccountNumber` keeps this page
      // masked exactly like the other screens that list the same account.
      // Guarded on presence: the helper answers an em dash for an empty value,
      // which beside the name would read as a number.
      //
      // A card on an unhealthy connection stays listed with its last known
      // figures; the warning icon beside the name says how old they are.
      render: (row) => {
        const warning = creditCardConnectionWarning(row)
        return (
          <span className="inline-flex items-center gap-1">
            <span className="inline-flex items-center gap-1">
              <span className="font-medium">{row.name}</span>
              {row.account_number ? (
                <span className="text-muted-foreground">
                  {formatAccountNumber(row.account_number)}
                </span>
              ) : null}
              <span className="ml-1">
                <CreditCardSourceBadge source={row.source} />
              </span>
            </span>
            {warning && <DataWarningIcon message={warning} />}
          </span>
        )
      },
    },
    {
      key: 'credit_limit',
      header: <span className="whitespace-nowrap">Total Credit Limit</span>,
      headerLabel: 'Total Credit Limit',
      headerHint: CREDIT_LIMIT_HINT,
      sortable: true,
      cellClassName: 'tabular-nums whitespace-nowrap',
      minWidth: '170px',
      render: (row) => effectiveValue(row, 'credit_limit', money(row.credit_limit)),
    },
    {
      key: 'balance',
      header: <span className="whitespace-nowrap">Current Balance (Usage)</span>,
      headerLabel: 'Current Balance (Usage)',
      headerHint: BALANCE_HINT,
      sortable: true,
      cellClassName: 'tabular-nums whitespace-nowrap',
      minWidth: '240px',
      // Signed as the vendor signs it: what is owed is negative. "View
      // transactions" needs both a balance and transactions to list, linked
      // and manual cards alike.
      render: (row) => (
        <span className="inline-flex items-center gap-2">
          {effectiveValue(row, 'balance', money(row.balance))}
          {row.balance !== null && row.has_transactions && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => onViewTransactions(row.id)}
              title="View transactions included in Current Balance"
              aria-label={`View transactions for ${row.name}`}
              className="h-6 w-6"
            >
              <ViewTransactionsIcon className="h-3.5 w-3.5" />
            </Button>
          )}
        </span>
      ),
    },
    {
      key: 'available_credit',
      header: <span className="whitespace-nowrap">Availability</span>,
      headerLabel: 'Availability',
      headerHint: AVAILABILITY_HINT,
      sortable: true,
      cellClassName: 'tabular-nums whitespace-nowrap',
      minWidth: '150px',
      // AC#8.2's own figure — limit minus usage, computed by the RPC — not
      // the vendor's `available_credit`. Empty whenever either operand is
      // missing; a real zero shows as $0.00, because a card with nothing
      // left is a fact and must not read as "unknown".
      render: (row) => money(row.availability),
    },
    {
      key: 'payment_due_date',
      header: <span className="whitespace-nowrap">Payment Due Date</span>,
      headerLabel: 'Payment Due Date',
      headerHint: PAYMENT_DUE_DATE_HINT,
      sortable: true,
      cellClassName: 'whitespace-nowrap',
      minWidth: '170px',
      render: (row) =>
        effectiveValue(
          row,
          'payment_due_date',
          row.payment_due_date ? formatDate(row.payment_due_date) : '—',
        ),
    },
    {
      key: 'statement_balance',
      header: <span className="whitespace-nowrap">Statement Balance</span>,
      headerLabel: 'Statement Balance',
      headerHint: STATEMENT_BALANCE_HINT,
      sortable: true,
      cellClassName: 'tabular-nums whitespace-nowrap',
      minWidth: '175px',
      // The RPC falls back to the printed total when the bank has not
      // reported the remainder — for some cards permanently — so the cell
      // says which of the two it is showing. Only for the integration's own
      // figure: a manual value (a manual card's, or an override) is what the
      // user typed, and no bank remainder applies to it.
      render: (row) =>
        row.source === 'linked' &&
        !row.statement_balance_is_manual &&
        row.remaining_statement_balance == null &&
        row.statement_balance != null ? (
          <PrintedStatementBalance value={row.statement_balance} />
        ) : (
          effectiveValue(row, 'statement_balance', money(row.statement_balance))
        ),
    },
    {
      key: 'note',
      header: <span className="whitespace-nowrap">Note</span>,
      headerLabel: 'Note',
      cellClassName: 'text-muted-foreground',
      minWidth: '160px',
      // Free text of any length: clamped to two lines, the full note in a tooltip.
      render: (row) => (row.note ? <CreditCardNote note={row.note} /> : '—'),
    },
    {
      key: 'last_updated',
      header: <span className="whitespace-nowrap">Last Updated</span>,
      headerLabel: 'Last Updated',
      sortable: true,
      cellClassName: 'whitespace-nowrap',
      minWidth: '170px',
      // When anything about this account last MOVED — balance or terms. A
      // trigger stamps it on change only, so a daily sync that reads the
      // same numbers leaves it where it was, which is what makes the column
      // worth reading.
      render: (row) => (row.data_updated_at ? formatDateTime(row.data_updated_at) : '—'),
    },
    {
      key: 'actions',
      header: 'Actions',
      disableHide: true,
      width: '56px',
      // Without `manage_banking` the kebab stays, disabled, so the column
      // does not shift between roles.
      render: (row) =>
        canManage ? (
          <ActionMenu
            ariaLabel={`Actions for ${row.name}`}
            align="end"
            items={[
              {
                id: 'edit',
                label: 'Edit',
                icon: <Pencil aria-hidden="true" className="h-4 w-4" />,
                onClick: () => onEdit(row.id),
              },
            ]}
          />
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            disabled
            aria-label={`Actions for ${row.name}`}
          >
            <MoreVertical aria-hidden="true" className="h-4 w-4" />
          </Button>
        ),
    },
  ]
}

/**
 * A figure of `field` as the column formats it, marked with the manual-edit
 * indicator when a manual value replaced the integration's on a linked card.
 */
function effectiveValue(
  row: CreditCardAvailabilityRow,
  field: CreditCardValueField,
  text: string,
): ReactNode {
  return row[`${field}_is_manual` as const] ? (
    <ManuallyEditedValue text={text} tooltip={manualEditTooltip(row, field)} />
  ) : (
    text
  )
}

/**
 * A figure the bank did not publish renders as an em dash, never as $0.00.
 *
 * The vendor is explicit that an absent field means the bank did not report it,
 * and a credit limit of zero reads as a card with no room left — a different
 * fact, and one an AP user would act on.
 *
 * @param value - the amount, or null when unpublished
 */
function money(value: number | null): string {
  return value === null ? '—' : formatCurrency(value)
}
